/**
 * PwaLaunchGate — wraps the root ("/") route.
 *
 * Behaviour:
 *   • Regular browser tab            → render Home unchanged (marketing site).
 *   • PWA + authenticated            → redirect straight to the user's dashboard.
 *   • PWA + not authenticated        → render PwaRoleSelect (Continue as User
 *                                       / Continue as Lawyer).
 *
 * The public website — landing, user login on the homepage, lawyer login link
 * in the footer — is untouched. Only the installed PWA gets the app-shell UX.
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePwaMode } from "@/hooks/usePwaMode";
import PwaRoleSelect from "@/pages/PwaRoleSelect";
import Home from "@user/pages/Home";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { ADMIN_BASE } from "@admin/constants/route";

function dashboardFor(user) {
  const role = user?.role;
  if (role === "admin") return ADMIN_BASE || "/";
  if (role === "lawyer") return LAWYER_BASE || "/";
  return "/dashboard";
}

export default function PwaLaunchGate() {
  const isPwa = usePwaMode();
  const { user, loading, login } = useAuth();

  // Website: totally unchanged.
  if (!isPwa) return <Home />;

  // PWA: wait for the /auth/me probe to complete before deciding.
  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#0A2540] text-white flex items-center justify-center"
        data-testid="pwa-loading"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="w-12 h-12 border-2 border-white/20 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={dashboardFor(user)} replace />;
  }

  return <PwaRoleSelect onLoginRequested={login} />;
}
